import { Column, TUseReportTable } from "domains/ReportBuilder/types";
import { useEffect, useMemo, useRef, useState } from "react";

export const useReportTable: TUseReportTable = ({ columns }) => {
    const maxWidth = 500;
    const tableMinWidth = columns.reduce((acc, col) => acc + col.minWidth, 0);

    const [columnWidths, setColumnWidths] = useState(() =>
        Object.fromEntries(
            columns.map((col) => [col.id, col.width ?? col.minWidth]),
        ),
    );

    const template = useMemo(
        () => columns.map((column) => `${columnWidths[column.id]}px`).join(' '),
        [columns, columnWidths],
    );

    const resizeRef = useRef<{
        columnId: string;
        startX: number;
        startWidth: number;
    } | null>(null);

    const tableWidth = Object.values(columnWidths).reduce(
        (sum, width) => sum + width,
        0,
    );

    const handleResizeStart = (e: React.MouseEvent, column: Column) => {
        e.preventDefault();

        resizeRef.current = {
            columnId: column.id,
            startX: e.clientX,
            startWidth: columnWidths[column.id],
        };
    };

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!resizeRef.current) {
                return;
            }

            const { columnId, startX, startWidth } = resizeRef.current;

            const delta = e.clientX - startX;

            const column = columns.find((c) => c.id === columnId);

            const width = Math.min(
                maxWidth,
                Math.max(column?.minWidth ?? 100, startWidth + delta),
            );

            setColumnWidths((prev) => ({
                ...prev,
                [columnId]: width,
            }));
        };

        const handleMouseUp = () => {
            resizeRef.current = null;
        };

        document.addEventListener('mousemove', handleMouseMove);

        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);

            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [columns]);
    return {
        tableMinWidth,
        template,
        handleResizeStart,
        tableWidth,
    }
}